import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import FetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/Queries/FetchWorkflowRun';
import fetchWorkflowRun from '@Application/WorkflowRuns/WorkflowRun/fetchWorkflowRun';
import { theme } from '@Guidelines/Theme/theme';
import useConnectivity from '@UI/Providers/PortsProvider/Hooks/Connectivity/useConnectivity';
import RecordingControls from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/RecordingControls';
import RecordingLevelMeter from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/RecordingLevelMeter';
import useInterviewCapture from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/Hooks/useInterviewCapture';
import useScreenWakeIndicator from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/Hooks/useScreenWakeIndicator';
import useStorageWarning from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/RecordInterviewPage/Hooks/useStorageWarning';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { ConfirmDialog, Skeleton } from '@verclam/ui-kit';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';

const skeletonStackStyle = css`
	width: 100%;
	max-width: 720px;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const messageCardStyle = css`
	width: 100%;
	max-width: 480px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.75rem;
	text-align: center;
	padding: 3rem ${theme.layout.gutter};
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surfaceMuted};
	color: ${theme.colors.inkSecondary};
`;

const messageTitleStyle = css`
	${theme.typography.stateTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const messageDescriptionStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const pageWrapperStyle = css`
	width: 100%;
	max-width: 720px;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const recordingCardStyle = (isNote: boolean) => css`
	background-color: ${isNote ? theme.states.success.solid : theme.colors.surfaceInverted};
	border-radius: ${theme.radii.xxlarge};
	padding: ${isNote ? 40 : 46}px 40px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: ${isNote ? 22 : 26}px;
	min-height: ${isNote ? 400 : 480}px;
	justify-content: center;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		padding: 32px 22px;
		min-height: ${isNote ? 360 : 420}px;
	}
`;

const headerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
`;

const eyebrowOnDarkStyle = css`
	${theme.typography.eyebrow}
	color: ${theme.colors.inkOnDarkMuted};
`;

const subjectNameStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 1.375rem;
	font-weight: ${theme.fontWeights.semibold};
	letter-spacing: -0.01em;
	color: ${theme.colors.inkOnDark};
`;

const preparationLineStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkOnDarkMuted};
`;

const durationStyle = css`
	font-family: ${theme.fonts.mono};
	font-size: clamp(2.5rem, 8vw, 4rem);
	font-weight: ${theme.fontWeights.medium};
	letter-spacing: -0.03em;
	color: ${theme.colors.inkOnDark};
`;

const stateBlockStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
`;

const recordingBadgeStyle = css`
	padding: 9px 16px;
	border-radius: ${theme.radii.pill};
	background-color: ${theme.colors.recording};
	color: ${theme.colors.inkOnDark};
	font-family: ${theme.fonts.body};
	font-weight: ${theme.fontWeights.semibold};
	font-size: ${theme.fontSizes.small};
`;

/** No token in `theme.ts` covers a translucent-white overlay on `colors.surfaceInverted`: this exact
 * value comes straight from the reference mockup (`Dixit - Refonte ADAR v8.dc.html`), which uses the same
 * literal rather than a design token for this one case (`RecordingControls.tsx` mirrors
 * it for the pause button). */
const pausedOverlayColor = 'rgba(255, 255, 255, 0.14)';

const pausedBadgeStyle = css`
	${recordingBadgeStyle}
	background-color: ${pausedOverlayColor};
`;

const subTextStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkOnDarkMuted};
	text-align: center;
	max-width: 300px;
`;

const wakeLockMentionStyle = css`
	${theme.typography.eyebrow}
	color: ${theme.colors.inkOnDarkMuted};
`;

const interruptionMentionStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkOnDarkMuted};
`;

const infoBannerStyle = css`
	background-color: ${theme.colors.surface};
	border-radius: ${theme.radii.large};
	padding: 18px 20px;
	display: flex;
	gap: 14px;
	flex-wrap: wrap;
	align-items: center;
	justify-content: space-between;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

const warningBannerStyle = css`
	${infoBannerStyle}
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
`;

const readyTitleStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 1.5rem;
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.inkOnDark};
	margin: 0;
`;

type RecordingTexts = {
	readonly eyebrow: string;
	readonly pausedHint: string;
	readonly recordingHint: string;
	readonly archivedNotice: string;
	readonly leaveTitle: string;
	readonly leaveMessage: string;
	readonly leaveAccept: string;
	readonly leaveReject: string;
};

const RECORDING_TEXTS: Readonly<Record<InterviewKind, RecordingTexts>> = Object.freeze({
	[InterviewKind.CONVERSATION]: {
		eyebrow: 'ENTRETIEN AVEC',
		pausedHint: 'Reprenez quand la personne est prête.',
		recordingHint: "Rien n'est envoyé tant que l'entretien n'est pas terminé.",
		archivedNotice: 'Ce dossier est archivé : un nouvel entretien ne peut plus être démarré.',
		leaveTitle: "Quitter l'entretien ?",
		leaveMessage: "Arrêter l'entretien en cours ? L'enregistrement sera conservé et transféré.",
		leaveAccept: "Arrêter l'entretien",
		leaveReject: "Continuer l'entretien",
	},
	[InterviewKind.NOTES]: {
		eyebrow: 'NOTE DICTÉE',
		pausedHint: 'Reprenez quand vous êtes prêt.',
		recordingHint: "Rien n'est envoyé tant que la prise de notes n'est pas terminée.",
		archivedNotice: 'Ce dossier est archivé : une nouvelle prise de notes ne peut plus être démarrée.',
		leaveTitle: 'Quitter la prise de notes ?',
		leaveMessage: "Arrêter la prise de notes en cours ? L'enregistrement sera conservé et transféré.",
		leaveAccept: 'Arrêter la prise de notes',
		leaveReject: 'Continuer la prise de notes',
	},
	[InterviewKind.TEXT]: {
		eyebrow: 'TEXTE SAISI',
		pausedHint: 'Reprenez quand vous êtes prêt.',
		recordingHint: "Rien n'est envoyé tant que le texte saisi n'est pas terminé.",
		archivedNotice: 'Ce dossier est archivé : un nouveau texte saisi ne peut plus être démarré.',
		leaveTitle: 'Quitter le texte saisi ?',
		leaveMessage: "Arrêter le texte saisi en cours ? L'enregistrement sera conservé et transféré.",
		leaveAccept: 'Arrêter le texte saisi',
		leaveReject: 'Continuer le texte saisi',
	},
});

type Props = {
	readonly subject: SubjectAddress;
	readonly localInterviewId: string;
};

/** `MM:SS`, from `CaptureSnapshot.recordedMs` — recorded time, pauses excluded. */
const formatDuration = (recordedMs: number): string => {
	const totalSeconds = Math.max(0, Math.floor(recordedMs / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Built as a single template string, never adjacent JSX text nodes: JSX collapses the whitespace
 * between a text node and an expression unpredictably, which would risk a missing space. */
const formatInterruptionMention = (interruptionCount: number): string => {
	const plural = interruptionCount > 1 ? 's' : '';

	return `${interruptionCount} interruption${plural} récupérée${plural} automatiquement.`;
};

/**
 * Owns V-19 end to end once the route's ids are known valid: the dossier's name (display only —
 * `useInterviewCapture` never reads it, `context` on the outbox item carries ids alone), the capture
 * session itself, the screen-wake mention, the storage/connectivity banners, and the confirmation
 * gate before leaving mid-capture.
 *
 * No visible "back"/"quitter" control is rendered: the maquette carries none for this screen (unlike
 * V-17/V-18, which both show one), consistent with "rien qui demande d'attention" on the one screen
 * used in front of the person. The leave confirmation instead guards the browser's own back
 * navigation (`popstate`) and full-page unload (`beforeunload`) — see the completion summary for the
 * residual limitation this implies.
 */
const InterviewRecordingContent: React.FC<Props> = ({ subject, localInterviewId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { user } = useStrictAuthenticatedUser();
	const ownerUserId = user.id;
	const isOnline = useConnectivity();
	const isScreenWakeHeld = useScreenWakeIndicator();

	const {
		isKnown: isSubjectKnown,
		canModify: canModifySubject,
		isArchived: isSubjectArchived,
		displayName: subjectName,
	} = useSubjectModifiability(subject);
	const isSubjectWriteRefused = isSubjectKnown && !canModifySubject;

	const {
		state: captureState,
		kind,
		preparationId,
		start,
		pause,
		resume,
		stop,
	} = useInterviewCapture({
		localInterviewId,
		ownerUserId,
	});

	// Cosmetic: the line under the subject's name. Capture is offline-first, so a failed read never
	// blocks the screen — the line is simply absent.
	const { data: preparation } = useStoreQuery({
		selector: (state) => state.workflowRunsStore.workflowRun,
		stopOn: (value) => preparationId === null || (value !== null && value.id === preparationId),
		query: () => {
			if (preparationId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchWorkflowRun(new FetchWorkflowRun(facilityId, subject, preparationId))).catch(
				() => undefined,
			);
		},
		deps: [facilityId, subject, preparationId],
	});
	const preparationLabel =
		preparation !== null && preparation.id === preparationId ? preparation.label : null;
	const isNote = kind === InterviewKind.NOTES;

	// Both entry states resolve away from this screen rather than render a message: `04-capture.md`
	// names them but gives each an arrow, not a button — an id the local store never hydrated with
	// sends the visitor back to the dossier, a capture already attached to a queue item sends them to
	// the upload page (`useInterviewCapture` also re-checks the latter, defensively, inside `start`).
	React.useEffect(() => {
		if (captureState.phase === 'interview-not-found') {
			navigate(buildSubjectPath(subject));
			return;
		}

		if (captureState.phase === 'already-captured') {
			navigate(`${buildSubjectPath(subject)}/local-interviews/${localInterviewId}/upload`);
		}
	}, [captureState.phase, subject, localInterviewId, navigate]);

	const isCaptureActive = captureState.phase === 'recording' || captureState.phase === 'paused';
	const isStorageLow = useStorageWarning(isCaptureActive);

	const [isLeaveConfirmationVisible, setIsLeaveConfirmationVisible] = React.useState(false);

	// Guards the browser's own back navigation while a capture is active: pushes a decoy history
	// entry, and re-pushes it on every `popstate` so the URL never actually changes before the user
	// has chosen — `ConfirmDialog` decides, not the browser.
	React.useEffect(() => {
		if (!isCaptureActive) {
			return undefined;
		}

		window.history.pushState(null, '', window.location.href);

		const handlePopState = (): void => {
			window.history.pushState(null, '', window.location.href);
			setIsLeaveConfirmationVisible(true);
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, [isCaptureActive]);

	// Guards a full-page unload (tab close, refresh, typed URL): the browser's own native prompt,
	// never styled, complementary to the `ConfirmDialog` above which only ever covers in-app back
	// navigation.
	React.useEffect(() => {
		if (!isCaptureActive) {
			return undefined;
		}

		const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
			event.preventDefault();
			event.returnValue = '';
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isCaptureActive]);

	const handleLeaveDialogHide = React.useCallback(() => {
		setIsLeaveConfirmationVisible(false);
	}, []);

	const handleLeaveAccept = React.useCallback(() => {
		setIsLeaveConfirmationVisible(false);
		stop();
	}, [stop]);

	const handleTogglePause = React.useCallback(() => {
		if (captureState.phase === 'paused') {
			resume();
			return;
		}

		if (captureState.phase === 'recording') {
			pause();
		}
	}, [captureState.phase, pause, resume]);

	// Unlike the creation screens this one does not fail closed: capture is offline-first, and an
	// unloaded record must not block a session on a device without network. Only a loaded, archived
	// record hides the start control, and a session already under way is never interrupted.
	const texts = RECORDING_TEXTS[kind ?? InterviewKind.CONVERSATION];

	// Capture starts as soon as the screen opens: the consent / « Commencer la dictée » click was the deliberate gesture.
	const hasAutoStartedRef = React.useRef(false);
	const canAutoStart = captureState.phase === 'ready' && !isSubjectWriteRefused;
	React.useEffect(() => {
		if (canAutoStart && !hasAutoStartedRef.current) {
			hasAutoStartedRef.current = true;
			start();
		}
	}, [canAutoStart, start]);

	if (captureState.phase === 'loading-interview' || captureState.phase === 'checking-permission') {
		return (
			<div css={skeletonStackStyle}>
				<Skeleton height="6rem" animation="wave" shape="rectangle" />
				<Skeleton height="20rem" animation="wave" shape="rectangle" />
			</div>
		);
	}

	// Neither renders a message: the effect above navigates away as soon as either phase is reached,
	// so this only covers the render that happens before that effect commits.
	if (captureState.phase === 'interview-not-found' || captureState.phase === 'already-captured') {
		return null;
	}

	if (captureState.phase === 'permission-denied') {
		return (
			<div css={messageCardStyle}>
				<p css={messageTitleStyle}>L'accès au microphone a été refusé.</p>
				<p css={messageDescriptionStyle}>
					Autorisez le microphone dans les réglages de votre navigateur, puis revenez sur cette
					page.
				</p>
			</div>
		);
	}

	if (captureState.phase === 'unsupported') {
		return (
			<div css={messageCardStyle}>
				<p css={messageTitleStyle}>Cet appareil ne prend pas en charge l'enregistrement audio.</p>
			</div>
		);
	}

	if (captureState.phase === 'format-unrecognized') {
		return (
			<div css={messageCardStyle}>
				<p css={messageTitleStyle}>Le format audio de cet appareil n'est pas reconnu.</p>
			</div>
		);
	}

	if (captureState.phase === 'terminal-failure') {
		return (
			<div css={messageCardStyle}>
				<p css={messageTitleStyle}>{captureState.reason}</p>
			</div>
		);
	}

	// 'ready' | 'starting' | 'stopping' | 'recording' | 'paused' from here on: the session card.
	// Computed through an immediately-invoked function, not a ternary on a boolean captured earlier,
	// so each branch narrows `captureState.phase` on its own — a plain boolean variable would not let
	// TypeScript infer `recordedMs`/`level`/`interruptionCount` are actually present.
	const sessionBody: React.ReactNode = (() => {
		if (captureState.phase === 'recording' || captureState.phase === 'paused') {
			const isPaused = captureState.phase === 'paused';

			return (
				<React.Fragment>
					<div css={durationStyle}>{formatDuration(captureState.recordedMs)}</div>
					<RecordingLevelMeter level={captureState.level} isPaused={isPaused} />
					<div css={stateBlockStyle}>
						<ConditionalLoader data={!isNote}>
							<span css={isPaused ? pausedBadgeStyle : recordingBadgeStyle}>
								{isPaused ? 'En pause' : 'Enregistrement en cours'}
							</span>
						</ConditionalLoader>
						<p css={subTextStyle}>
							{!isOnline
								? 'Hors réseau : la capture continue en local.'
								: isNote
									? 'Parlez normalement. Terminez quand vous avez tout dit.'
									: isPaused
										? texts.pausedHint
										: texts.recordingHint}
						</p>
					</div>
					<RecordingControls
						phase={isPaused ? 'paused' : 'recording'}
						canPause={!isNote}
						stopLabel={isNote ? 'Terminer' : 'Arrêter'}
						isStopping={false}
						onTogglePause={handleTogglePause}
						onStop={stop}
					/>
					{captureState.interruptionCount > 0 ? (
						<p css={interruptionMentionStyle}>
							{formatInterruptionMention(captureState.interruptionCount)}
						</p>
					) : null}
				</React.Fragment>
			);
		}

		return (
			<React.Fragment>
				<p css={readyTitleStyle}>
					{captureState.phase === 'stopping' ? 'Arrêt en cours…' : 'Démarrage…'}
				</p>
				<ConditionalLoader data={captureState.phase === 'ready' && isSubjectArchived}>
					<p css={subTextStyle}>{texts.archivedNotice}</p>
				</ConditionalLoader>
			</React.Fragment>
		);
	})();

	return (
		<div css={pageWrapperStyle}>
			<div css={recordingCardStyle(isNote)}>
				{isNote ? (
					<span css={eyebrowOnDarkStyle}>{texts.eyebrow}</span>
				) : (
					<div css={headerStyle}>
						<span css={eyebrowOnDarkStyle}>{texts.eyebrow}</span>
						<span css={subjectNameStyle}>{subjectName ?? '…'}</span>
						<ConditionalLoader data={preparationLabel}>
							{(label) => <span css={preparationLineStyle}>{label}</span>}
						</ConditionalLoader>
					</div>
				)}

				{sessionBody}

				{isScreenWakeHeld ? <span css={wakeLockMentionStyle}>ÉCRAN MAINTENU ALLUMÉ</span> : null}
			</div>

			<div css={infoBannerStyle}>
				{isNote
					? 'À la fin, la note est transcrite puis ajoutée aux éléments de la préparation. Rien n’est généré.'
					: "L'audio est écrit localement au fil de la capture. Hors réseau, l'enregistrement continue : rien n'est bloqué."}
			</div>

			{isStorageLow ? (
				<div css={warningBannerStyle}>
					L'espace de stockage disponible sur cet appareil est faible. La capture continue, mais
					pensez à transférer les enregistrements en attente dès que possible.
				</div>
			) : null}

			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isLeaveConfirmationVisible}
				onHide={handleLeaveDialogHide}
				header={texts.leaveTitle}
				message={texts.leaveMessage}
				accept={handleLeaveAccept}
				reject={handleLeaveDialogHide}
				acceptLabel={texts.leaveAccept}
				rejectLabel={texts.leaveReject}
			/>
		</div>
	);
};

export default InterviewRecordingContent;
